<template>
    <button
        :disabled="disabled"
        :style="styles"
        :class="[
            `-${size}`,
            `-${view}`,
            `-${iconAlignment}`,
            { '-no-icon': !icon },
            { '-only-icon': icon && !ctaText },
            { '-full-width': fullWidth }
        ]"
    >
        <Icon v-if="isLoading" icon="LoaderBtn" />
        <Icon v-else-if="icon" :icon="icon" />
        <span v-if="ctaText">
            {{ ctaText }}
        </span>
    </button>
</template>

<script setup>
import Icon from './Icon.vue';
import { computed } from 'vue';

const props = defineProps({
    size: {
        type: String,
        default: 'md'
    },
    view: {
        type: String,
        default: 'primary'
    },
    ctaText: String,
    icon: String,
    iconAlignment: String,
    isLoading: Boolean,
    width: String,
    disabled: Boolean,
    fullWidth: Boolean
});

const styles = computed(() => (props.width ? { width: props.width } : null));
</script>

<style lang="scss" scoped>
@import '../assets/styles/_main.scss';

button {
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 10px;
    gap: 8px;
    cursor: pointer;

    @include fz-14;
    font-weight: 500;
    border: unset;
    background-color: unset;
    box-sizing: border-box;
    outline: none;

    @include transition-default;
    width: fit-content;

    &.-no-wrap {
        text-wrap: nowrap;
    }
    &.-right {
        flex-direction: row-reverse;
    }

    &.-no-icon {
        &.-md,
        &.-lg {
            padding: 0 12px;
        }
    }

    &.-full-width {
        width: 100%;
    }

    /* ---------- Sizes ---------- */
    &.-xl {
        height: 40px;
        padding: 0 12px;

        &.-only-icon {
            padding: unset;

            &:not(.-full-width) {
                width: 40px;
            }
        }
    }

    &.-lg {
        height: 36px;
        padding: 0 12px;

        &.-only-icon {
            padding: unset;

            &:not(.-full-width) {
                width: 36px;
            }
        }
    }

    &.-md {
        height: 32px;
        padding: 0 12px;
        border-radius: 8px;

        &.-only-icon {
            padding: unset;

            &:not(.-full-width) {
                width: 32px;
            }
        }
    }

    &.-sm {
        height: 28px;
        padding: 0 8px;
        border-radius: 8px;

        &.-only-icon {
            padding: unset;

            &:not(.-full-width) {
                width: 28px;
            }
        }
    }

    /* ----------- Views ----------- */
    &.-primary {
        background-color: $primary-accent;
        color: $text-light;

        &:hover {
            background-color: $primary-deep;
        }

        &:active {
            background-color: $primary-deep;
        }

        &:disabled {
            background-color: $card-neutral-background;
            color: $text-low-emphasis;
        }
    }

    &.-secondary {
        border: 1px solid $border;
        background-color: $card-background;

        span {
            color: $text-high-emphasis;
        }

        &:hover {
            border-color: $border;
            background-color: $card-neutral-background;
        }

        &:active {
            border-color: $primary-accent;
            background-color: $card-background;
        }

        &:disabled {
            border: unset;
            background-color: $card-neutral-background;
            color: $text-low-emphasis;
        }
    }

    &.-tertiary {
        color: $primary-accent;

        &:hover {
            background-color: $card-primary-background-light;
        }

        &:active {
            background-color: $card-primary-background-light;
        }

        &:disabled {
            color: $text-low-emphasis;
        }
    }

    &.-no-border {
        color: $text-mid-emphasis;

        &:hover {
            color: $text-high-emphasis;
        }

        &:active {
            color: $text-high-emphasis;
        }

        &:disabled {
            color: $text-low-emphasis;
        }
    }

    &.-crucial {
        background-color: $text-error-accent;
        color: $text-light;

        &:hover {
            background-color: $error-deep;
        }

        &:active {
            background-color: $error-deep;
        }

        &:disabled {
            background-color: $card-neutral-background;
            color: $text-low-emphasis;
        }
    }

    &:disabled {
        cursor: not-allowed;
    }
}

body[theme='dark'] button {
    &.-secondary {
        border: 1px solid $border-secondary;
        background-color: $background-secondary;

        &:hover {
            border: 1px solid $border-secondary;
            background-color: $card-neutral-background;
        }

        &:active {
            border-color: $primary-accent;
            background-color: $card-background;
        }

        &:disabled {
            border: unset;
            background-color: $card-neutral-background;
            color: $text-low-emphasis;
        }
    }
}
</style>
